import React, { useEffect, useMemo, useState } from "react";
import { api } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { money } from "../lib/format";
import { Button } from "./ui/button";
import { Calculator, UsersThree, ArrowRight, WhatsappLogo } from "@phosphor-icons/react";

export const ReferralCalculator = ({ cfg: cfgProp, onStart, refCode }) => {
  const { t } = useI18n();
  const [cfg, setCfg] = useState(cfgProp || null);
  const [n, setN] = useState(6);
  useEffect(() => {
    if (cfgProp) { setCfg(cfgProp); return; }
    api.get("/referral/public-config").then((r) => { if (r.data && r.data.active !== false) setCfg(r.data); }).catch(() => {});
  }, [cfgProp]);
  const calc = useMemo(() => {
    const l2 = Number(cfg?.level2_amount || 0), l3 = Number(cfg?.level3_amount || 0), l4 = Number(cfg?.level4_amount || 0);
    const per2 = l2 / 2, per3 = l3 / 4, per4 = l4 / 8;
    const n2 = Math.min(n, 2);
    const n3 = Math.min(Math.max(n - 2, 0), 4);
    const n4 = Math.min(Math.max(n - 6, 0), 8);
    return { total: n2 * per2 + n3 * per3 + n4 * per4, n2, n3, n4, per2, per3, per4, full: n >= 14 };
  }, [cfg, n]);
  if (!cfg) return null;
  const cur = cfg.currency || "USD";
  const shareLink = refCode ? `${window.location.origin}/register?ref=${refCode}` : "";
  const shareHref = refCode ? `https://wa.me/?text=${encodeURIComponent(`${t("calc_share_msg").replace("{n}", n)}\n\n${shareLink}`)}` : "";
  return (
    <div className="rounded-3xl bg-white border border-emerald-100 shadow-lg p-6 sm:p-8" data-testid="referral-calculator">
      <div className="flex items-center gap-2 mb-1"><Calculator size={22} weight="duotone" className="text-emerald-700" /><h3 className="font-display font-black text-emerald-950 text-xl sm:text-2xl">{t("calc_title")}</h3></div>
      <p className="text-slate-600 mb-6">{t("calc_sub")}</p>
      <div className="grid md:grid-cols-2 gap-6 items-center">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5"><UsersThree size={18} weight="fill" className="text-emerald-600" /> {t("calc_friends")}</label>
            <span className="font-mono-num text-2xl font-black text-emerald-700" data-testid="calc-count">{n}</span>
          </div>
          <input type="range" min="1" max="14" value={n} onChange={(e) => setN(Number(e.target.value))} data-testid="calc-slider" className="w-full accent-emerald-600 h-2 cursor-pointer" />
          <div className="flex justify-between text-[11px] text-slate-400 mt-1"><span>1</span><span>14+</span></div>
          <div className="mt-4 space-y-1.5 text-sm">
            {calc.n2 > 0 && <div className="flex justify-between"><span className="text-slate-500">{t("level")} 2 · {calc.n2} × {money(calc.per2, cur)}</span><span className="font-mono-num font-semibold text-emerald-800">{money(calc.n2 * calc.per2, cur)}</span></div>}
            {calc.n3 > 0 && <div className="flex justify-between"><span className="text-slate-500">{t("level")} 3 · {calc.n3} × {money(calc.per3, cur)}</span><span className="font-mono-num font-semibold text-emerald-800">{money(calc.n3 * calc.per3, cur)}</span></div>}
            {calc.n4 > 0 && <div className="flex justify-between"><span className="text-slate-500">{t("level")} 4 · {calc.n4} × {money(calc.per4, cur)}</span><span className="font-mono-num font-semibold text-emerald-800">{money(calc.n4 * calc.per4, cur)}</span></div>}
          </div>
        </div>
        <div className="rounded-2xl bg-emerald-900 text-white p-6 text-center">
          <p className="text-sm text-emerald-200">{t("calc_you_earn")}</p>
          <p className="font-mono-num font-black text-4xl sm:text-5xl text-emerald-300 mt-2 mb-1 break-words" data-testid="calc-total">{money(calc.total, cur)}</p>
          {calc.full && <p className="text-xs text-amber-300 font-semibold mt-2">{t("calc_full")}</p>}
          {onStart && (
            <Button data-testid="calc-cta" onClick={onStart} className="mt-4 bg-amber-400 hover:bg-amber-300 text-emerald-950 font-bold gap-2">{t("invite_earn_cta")} <ArrowRight size={18} weight="bold" /></Button>
          )}
          {refCode && (
            <a data-testid="calc-share-wa" href={shareHref} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-[#25D366] hover:bg-[#1eb85a] text-white font-bold px-5 h-11 transition-colors">
              <WhatsappLogo size={20} weight="fill" /> {t("calc_share_wa")}
            </a>
          )}
        </div>
      </div>
      <p className="text-[11px] text-slate-400 mt-5">{t("calc_disclaimer")}</p>
    </div>
  );
};
